import React, { useState, useEffect, useCallback } from 'react';
import { Form, FormGroup, Label, Input, Button, Container, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import ServicePicker from "./ServicePicker";
import TimePicker from "./TimePicker";
import CustomTimePicker from "./CustomTimePicker";
import moment from 'moment';
import api from '../services/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CurrentSelection from './CurrentSelection';

const BookingForm = ({ event = {}, bookingDate: initialDate, bookingTime, service, resources, discountCodes, onSubmit, newSource, resourceType }) => {

  const defaultService = {
    id: 0,
    title: 'Unknown',
    duration: 60
  };

  const customService = {
    id: 999,
    title: 'Custom',
    duration: moment.duration(event.end - event.start).asMinutes()
  };

  const selectedServiceInit = event.id
    ? (event.serviceDetails || customService)
    : (service || defaultService);

  const [organization, setOrganization] = useState(null);
  const [sendEmail, setSendEmail] = useState(false);
  const [bookingDate, setBookingDate] = useState(initialDate || new Date());
  const [selectedService, setSelectedService] = useState(selectedServiceInit);
  const [eventId] = useState(event.id || 0);
  const [name, setName] = useState(event.customer_name || newSource || '');
  const [phone, setPhone] = useState(event.customer_phone || '');
  const [email, setEmail] = useState(event.customer_email || '');
  const [notes, setNotes] = useState(event.notes || '');
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(event.first_time_user || false);
  const [selectedResource, setSelectedResource] = useState(event.resourceLocked ? event.resource : 0);
  const [startTime, setStartTime] = useState(bookingTime || '');
  const [endTime, setEndTime] = useState(event.end || '');
  const [source] = useState(newSource || event.source || 'Internal');
  const [isEditMode] = useState(!!event.id);
  const [isValid, setIsValid] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(!isEditMode);
  const [discountCode, setDiscountCode] = useState(event.discount_code || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [filteredResources, setFilteredResources] = useState(resources);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [leagueScoreValue, setLeagueScoreValue] = useState('');
  const [leagueScoreRecordId, setLeagueScoreRecordId] = useState(null);
  const [scoreSaving, setScoreSaving] = useState(false);
  const [scoreLoadError, setScoreLoadError] = useState('');
  const [resolvedScoreTeamId, setResolvedScoreTeamId] = useState(null);
  const [resolvedLeagueWeekId, setResolvedLeagueWeekId] = useState(null);
  const [existingLeagueScore, setExistingLeagueScore] = useState(null);
  const [existingScoreLoading, setExistingScoreLoading] = useState(false);

  const getFilteredResourcesForService = useCallback((serviceToFilter) => {
    const resourceTypeId = serviceToFilter?.resource_type || Number(resourceType) || null;

    if (!resourceTypeId) {
      return resources;
    }

    const matchingResources = resources.filter(r => r.resource_type === Number(resourceTypeId));
    const resourceCount = serviceToFilter?.resource_count || 1;

    return matchingResources.filter((_, index) => {
      return index <= matchingResources.length - resourceCount;
    });
  }, [resources, resourceType]);

  const directLeagueWeekId = event?.league_week?.id || (typeof event?.league_week === 'number' ? event.league_week : null) || event?.league_week_id || null;
  const leagueWeekNumber = event?.league_date?.week || event?.league_week?.week || null;
  const leagueId = event?.league?.id || event?.league_id || event?.league_team?.league || event?.league_team?.league_id || null;
  const leagueTeamObjectId = event?.league_team?.id || event?.league_team_id || (typeof event?.league_team === 'number' ? event.league_team : null);
  const scoreTeamId =
    (typeof event?.league_team?.team === 'number' ? event.league_team.team : null)
    || event?.league_team?.team?.id
    || (typeof event?.team === 'number' ? event.team : null)
    || event?.team?.id
    || event?.team_id
    || null;
  const canEnterLeagueScore = Boolean(isEditMode && (event?.league_date || event?.league_team || event?.league_week));

  useEffect(() => {
    setResolvedScoreTeamId(scoreTeamId || null);
  }, [scoreTeamId, event?.id]);

  useEffect(() => {
    setResolvedLeagueWeekId(directLeagueWeekId || null);
  }, [directLeagueWeekId, event?.id]);

  const resolveLeagueWeekId = useCallback(async () => {
    let weekIdForScore = resolvedLeagueWeekId || directLeagueWeekId;

    if (!weekIdForScore && leagueId && leagueWeekNumber !== null && leagueWeekNumber !== undefined) {
      const weeksResponse = await api.get('/league-weeks/', {
        params: { league: leagueId },
      });

      const weeks = Array.isArray(weeksResponse.data) ? weeksResponse.data : [];
      const matchedWeek = weeks.find((w) => Number(w?.week) === Number(leagueWeekNumber));

      weekIdForScore = matchedWeek?.id || null;
      if (weekIdForScore) {
        setResolvedLeagueWeekId(weekIdForScore);
      }
    }

    return weekIdForScore;
  }, [resolvedLeagueWeekId, directLeagueWeekId, leagueId, leagueWeekNumber]);

  useEffect(() => {
    setFilteredResources(getFilteredResourcesForService(selectedService));
  }, [selectedService, getFilteredResourcesForService]);

  const handleServiceChange = (service) => {
    setSelectedService(service);
    setSelectedResource(0);

    // Filter resources immediately on service change.
    // If service has no resource_type (e.g. custom), fall back to calendar's selected resource type.
    setFilteredResources(getFilteredResourcesForService(service));

    if (service.id !== 999) {
      if (startTime) {
        setEndTime(moment(startTime).add(service.duration, 'minutes'));
        setShowTimePicker(false);
      } else {
        setShowTimePicker(true);
      }
    } else {
      setShowTimePicker(true);
    }
  };

  useEffect(() => {
    setIsValid(name.trim() !== '');
  }, [name]);

  useEffect(() => {
    api.get("/get-org-details/")
      .then((response) => {
        setOrganization(response.data);
      });
  }, []);

  // useEffect(() => {
  //   setSendEmail(organization?.send_email);
  // }, [organization]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    console.log(name, value, checked);

    switch (name) {
      case 'name':
        setName(value);
        break;
      case 'phone':
        setPhone(value);
        break;
      case 'email':
        setEmail(value);
        break;
      case 'notes':
        setNotes(value);
        break;
      case 'discountCode':
        setDiscountCode(value === '' ? '' : parseInt(value));
        break;
      case 'selectedResource':
        setSelectedResource(value);
        break;
      case 'isFirstTimeUser':
        setIsFirstTimeUser(checked);
        break;
      case 'sendEmail':
        setSendEmail(checked);
        break;
      default:
        break;
    }
  };

  const handleTimeChange = (selectedStartTime) => {
    setStartTime(selectedStartTime);
    setEndTime(moment(selectedStartTime).add(selectedService.duration, 'minutes'));
    setShowTimePicker(false);
  };

  const handleCustomTimeChange = (selectedStartTime, selectedEndTime, duration) => {
    setStartTime(selectedStartTime);
    setEndTime(selectedEndTime);
    setSelectedService({ id: 999, title: 'Custom', duration });
    setShowTimePicker(false);
  };

  const changeService = () => {
    setSelectedService({ id: 0, title: '', duration: 60 });
    setShowTimePicker(true);
  };

  const changeTime = () => {
    setShowTimePicker(true);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(!showDatePicker);
  };

  const openDatePicker = () => {
    setShowDatePicker(true);
  };

  const handleDateChange = (newDate) => {
    setBookingDate(newDate);
    setStartTime('');
    setEndTime('');
    setShowDatePicker(false);
    setShowTimePicker(true);
  };

  const buildEventObject = () => {
    const finalSelectedResource = Number(selectedResource);

    return {
      customer_name: name,
      customer_phone: phone,
      customer_email: email,
      notes,
      first_time_user: isFirstTimeUser,
      resource: finalSelectedResource !== 0 ? finalSelectedResource : null,
      resourceLocked: finalSelectedResource !== 0,
      resource_type: event.resource_type || resourceType || null,
      service: selectedService.id === 999 ? null : selectedService.id,
      start: moment(startTime).toISOString(),
      end: moment(endTime).toISOString(),
      discount_code: discountCode,
      sendEmail: sendEmail,
      source: source || 'Internal',
    };
  };

  const cancelEvent = async () => {
    if (isRemoving) return; // Prevent multiple clicks
    
    setIsRemoving(true);
    const eventData = buildEventObject();

    try {
      const flag = sendEmail ? "&email" : "";
      await api.get(`/cancel-booking/?event=${event.guid}${flag}`, eventData);
      balanceEvents();
    } catch (error) {
      console.error('Error canceling event:', error.response?.data || error.message);
      setIsRemoving(false);
    }
  };

  const addEvent = async () => {
    const eventData = buildEventObject();

    try {
      await api.post('/events/', eventData);
      balanceEvents();
    } catch (error) {
      console.error('Error adding event:', error.response?.data || error.message);
      if (error.response?.data) {
        setErrorMessage(error.response.data.detail || 'An error occurred while creating the booking');
        setErrorModalOpen(true);
        setIsSubmitting(false);
      }
    }
  };

  const updateEvent = async () => {
    const eventData = buildEventObject();

    try {
      await api.put(`/events/${event.id}/`, eventData);
      balanceEvents();
    } catch (error) {
      console.error('Error updating event:', error.response?.data || error.message);
      if (error.response?.data) {
        setErrorMessage(error.response.data.detail || 'An error occurred while updating the booking');
        setErrorModalOpen(true);
        setIsSubmitting(false);
      }
    }
  };

  const balanceEvents = async () => {
    onSubmit(new Date(startTime));
  };

  const toggleScoreModal = () => {
    setScoreModalOpen((prev) => !prev);
  };

  const fetchExistingLeagueScore = useCallback(async ({ applyToModal = false } = {}) => {
    let teamIdForScore = resolvedScoreTeamId || scoreTeamId;
    const weekIdForScore = await resolveLeagueWeekId();

    // Some event payloads include only league_team id; resolve real team id lazily.
    if (!teamIdForScore && leagueTeamObjectId) {
      const leagueTeamResponse = await api.get(`/league-teams/${leagueTeamObjectId}/`);
      teamIdForScore = leagueTeamResponse.data?.team?.id || leagueTeamResponse.data?.team || leagueTeamResponse.data?.team_id || null;
      if (teamIdForScore) {
        setResolvedScoreTeamId(teamIdForScore);
      }
    }

    if (!weekIdForScore || !teamIdForScore) {
      return null;
    }

    const response = await api.get('/league-week-team-scores/', {
      params: {
        ...(leagueId ? { league: leagueId } : {}),
        league_week: weekIdForScore,
        team: teamIdForScore,
      },
    });

    const scores = Array.isArray(response.data) ? response.data : [];
    const existing = scores.find((entry) => {
      const matchesWeek = (entry?.league_week === weekIdForScore) || (entry?.league_week?.id === weekIdForScore);
      const entryTeamId = entry?.team?.id || entry?.team || entry?.team_id;
      const matchesTeam = entryTeamId === teamIdForScore;
      return matchesWeek && matchesTeam;
    }) || scores[0] || null;

    if (applyToModal) {
      setLeagueScoreRecordId(existing?.id || null);
      setLeagueScoreValue(existing?.score ?? '');
    }

    setExistingLeagueScore(existing?.score ?? null);
    return existing;
  }, [
    resolvedScoreTeamId,
    scoreTeamId,
    resolveLeagueWeekId,
    leagueTeamObjectId,
    leagueId,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadExisting = async () => {
      if (!canEnterLeagueScore) {
        if (isMounted) {
          setExistingLeagueScore(null);
        }
        return;
      }

      if (isMounted) {
        setExistingScoreLoading(true);
      }

      try {
        await fetchExistingLeagueScore();
      } catch (error) {
        console.error('Error loading existing league score:', error.response?.data || error.message);
        if (isMounted) {
          setExistingLeagueScore(null);
        }
      } finally {
        if (isMounted) {
          setExistingScoreLoading(false);
        }
      }
    };

    loadExisting();

    return () => {
      isMounted = false;
    };
  }, [event?.id, canEnterLeagueScore, fetchExistingLeagueScore]);

  const openScoreModal = async () => {
    if (!canEnterLeagueScore) {
      return;
    }

    setScoreLoadError('');
    setLeagueScoreValue('');
    setLeagueScoreRecordId(null);
    setScoreModalOpen(true);

    try {
      const existing = await fetchExistingLeagueScore({ applyToModal: true });
      const hasResolvedWeek = Boolean(resolvedLeagueWeekId || directLeagueWeekId);
      const hasResolvedTeam = Boolean(resolvedScoreTeamId || scoreTeamId || leagueTeamObjectId);

      if (!existing && (!hasResolvedWeek || !hasResolvedTeam)) {
        setScoreLoadError('Unable to resolve league week/team for this booking.');
      }
    } catch (error) {
      console.error('Error loading league score:', error.response?.data || error.message);
      setScoreLoadError('Unable to load existing score. You can still enter a new value.');
    }
  };

  const saveLeagueScore = async () => {
    const teamIdForScore = resolvedScoreTeamId || scoreTeamId;
    const weekIdForScore = await resolveLeagueWeekId();

    if (leagueScoreValue === '') {
      setScoreLoadError('Score is required.');
      return;
    }

    if (!weekIdForScore || !teamIdForScore) {
      setScoreLoadError('Unable to resolve league week/team for this booking.');
      return;
    }

    const parsedScore = Number(leagueScoreValue);
    if (Number.isNaN(parsedScore) || !Number.isInteger(parsedScore)) {
      setScoreLoadError('Score must be a whole number.');
      return;
    }

    setScoreSaving(true);
    setScoreLoadError('');

    const payload = {
      ...(leagueId ? { league: leagueId } : {}),
      league_week: weekIdForScore,
      team: teamIdForScore,
      score: parsedScore,
    };

    // Include league_team if API serializer accepts it for richer linkage.
    if (leagueTeamObjectId) {
      payload.league_team = leagueTeamObjectId;
    }

    try {
      if (leagueScoreRecordId) {
        await api.put(`/league-week-team-scores/${leagueScoreRecordId}/`, payload);
      } else {
        const response = await api.post('/league-week-team-scores/', payload);
        setLeagueScoreRecordId(response.data?.id || null);
      }
      setExistingLeagueScore(parsedScore);
      setScoreModalOpen(false);
    } catch (error) {
      console.error('Error saving league score:', error.response?.data || error.message);
      const detail = error.response?.data?.detail || 'Unable to save score.';
      setScoreLoadError(detail);
    } finally {
      setScoreSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent multiple submits

    setIsSubmitting(true);

    if (isEditMode) {
      await updateEvent();
    } else {
      await addEvent();
    }
  };

  const renderStep = () => {
    if (showDatePicker) {
      return (
        <Container>
          <Row>
            <Col sm="8">
              <h5>{moment(bookingDate).format('dddd MMMM Do')}</h5>
            </Col>
            {isEditMode && (
              <Col sm="4" className="text-end">
                <Button outline size='sm' onClick={openDatePicker}>
                  Change Date
                </Button>
              </Col>
            )}
          </Row>
          <Row>
            <Col>
              <DatePicker
                selected={bookingDate}
                onChange={handleDateChange}
                popperPlacement='bottom'
                inline
              />
            </Col>
          </Row>
        </Container>
      );
    }

    if (selectedService.id === 0) {
      return (
        <ServicePicker
          selectedDate={bookingDate}
          selectedTime={startTime}
          selectedService={selectedService}
          selectedEvent={event}
          event={eventId}
          onServiceSelected={handleServiceChange}
          onCustomTimeChange={handleCustomTimeChange}
          resourceType={resourceType}
        />
      );
    }

    if (showTimePicker) {
      return (
        <Container>
          <Row className="mt-3">
            {selectedService.id === 999 ? (
              <CustomTimePicker key={startTime} bookingDate={bookingDate} selectedStart={startTime} selectedEnd={endTime} onTimeSelected={handleCustomTimeChange} />
            ) : (
              <TimePicker key={startTime} bookingDate={bookingDate} service={selectedService} event={eventId} onTimeSelected={handleTimeChange} />
            )}
          </Row>
        </Container>
      );
    }

    return (
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col>
            <h5 className="fw-bold mb-4">Customer Information</h5>
            <FormGroup>
              <Label for="name" className='fw-semibold'>Name</Label>
              <Input type="text" name="name" id="name" value={name || ''} onChange={handleChange} placeholder="Enter customer name" />
            </FormGroup>
            <FormGroup>
              <Label for="phone" className='fw-semibold'>Phone Number</Label>
              <Input type="tel" name="phone" id="phone" value={phone || ''} onChange={handleChange} placeholder="Enter customer phone number" />
            </FormGroup>
            <FormGroup>
              <Label for="email" className='fw-semibold'>Email</Label>
              <Input type="email" name="email" id="email" value={email || ''} onChange={handleChange} placeholder="Enter customer email" />
            </FormGroup>
            <FormGroup check>
              <Label check className='mb-2'>
                <Input type="checkbox" name="isFirstTimeUser" checked={!!isFirstTimeUser} onChange={handleChange} className='me-2 border-2 border-dark' />
                First-time user
              </Label>
            </FormGroup>
            <FormGroup>
              <div>
                <Label check className='mb-2'>
                  <Input
                    type="radio"
                    name="discountCode"
                    value=""
                    checked={!discountCode}
                    onChange={handleChange}
                    className='me-2 border-2 border-dark'
                  />
                  None
                </Label>
                <br />
              </div>
              {discountCodes.map((discount, index) => (
                <div key={discount.code || index}>
                  <Label check className='mb-2'>
                    <Input
                      type="radio"
                      name="discountCode"
                      value={discount.id}
                      checked={parseInt(discountCode) === discount.id}
                      onChange={handleChange}
                      className='me-2 border-2 border-dark'
                    />
                    {discount.description}
                  </Label>
                  <br />
                </div>
              ))}
            </FormGroup>
          </Col>
          <Col>
            <h5 className="fw-bold mb-4">Additional Details</h5>
            <FormGroup>
              <Label for="notes" className='fw-semibold'>Notes</Label>
              <Input type="textarea" name="notes" id="notes" value={notes || ''} onChange={handleChange} placeholder="Add any notes" />
              <div>
                <small>
                  {event.league_date && (<><strong>League Notes:</strong> Week {event.league_date?.week} {event.league_date?.notes && (<> - {event.league_date?.notes}</>)}<br /></>)}
                  {event.league_team?.players?.length > 0 && (
                    <>
                      <small>
                        <ul className="mb-1 mt-1 ps-3">
                          {event.league_team.players.map((player) => (
                            <li key={player.id}>
                              {player.first_name} {player.last_name}
                              {player.email && (
                                <> — <a href={`mailto:${player.email}`}>{player.email}</a></>
                              )}
                            </li>
                          ))}
                        </ul>
                      </small>
                    </>
                  )}
                  {event.customerNotes && (<><strong>Customer Notes:</strong> {event.customerNotes}<br /></>)}
                </small>
              </div>
              {canEnterLeagueScore && (
                <Row className="mt-2">
                  <Col>
                    <div className="small text-muted d-flex align-items-center gap-2 flex-wrap">
                      <span>
                        <strong>League Score:</strong>{' '}
                        {existingScoreLoading ? 'Loading...' : (existingLeagueScore ?? 'Not entered')}
                      </span>
                      <Button color="secondary" size="sm" outline onClick={openScoreModal}>
                        Enter Score
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="resources" className='fw-semibold'>Simulator Assignment</Label>
              <br />
              <Label check className='mb-2'>
                <Input type="radio" name="selectedResource" value="0" checked={parseInt(selectedResource) === 0} onChange={handleChange} className='border-2 border-dark me-2' />
                Best available, i.e. let the system decide
              </Label>
              {filteredResources.map((resource, index) => {
                const resourceCount = selectedService?.resource_count || 1;
                const allMatchingResources = resources.filter(r => r.resource_type === selectedService?.resource_type);
                const startIndex = allMatchingResources.findIndex(r => r.id === resource.id);
                const endResource = allMatchingResources[startIndex + resourceCount - 1];
                const displayText = resourceCount > 1 && endResource 
                  ? `${resource.title} - ${endResource.title}`
                  : resource.title;
                
                return (
                  <div key={resource.id}>
                    <Label check className='mb-2'>
                      <Input type="radio" name="selectedResource" value={resource.id} checked={parseInt(selectedResource) === resource.id} onChange={handleChange} className='border-2 border-dark me-2' />
                      {displayText}
                    </Label>
                    <br />
                  </div>
                );
              })}
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
          </Col>
          <Col>
            <Row className='mb-3'>
              <Col>
                <Button block type="submit" color="primary" disabled={!isValid || isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </Col>
              {isEditMode && (
                <Col>
                  <Button block color="danger" onClick={cancelEvent} disabled={isRemoving}>
                    {isRemoving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Removing...
                      </>
                    ) : (
                      'Remove Booking'
                    )}
                  </Button>
                </Col>
              )}
            </Row>
            {organization?.send_email && (
              <Row>
                <Col className="text-center border p-2">
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        name="sendEmail"
                        checked={sendEmail}
                        onChange={handleChange}
                        className='me-2 border-2 border-dark' />
                      Send confirmation email
                    </Label>
                  </FormGroup>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Form>
    );
  };

  const renderPastDay = () => (
    <Container>
      <Row>
        <Col className="text-center mt-3">
          <p>No available times found. Use the custom service option to book in the past.</p>
        </Col>
      </Row>
    </Container>
  );

  const bookingDateTime = startTime ? moment(startTime) : moment(bookingDate).endOf('day');
  const now = moment();

  const toggleErrorModal = () => {
    setErrorModalOpen(!errorModalOpen);
    if (errorModalOpen) {
      setErrorMessage('');
    }
  };

  return (
    <Container>
      <Row>
        <CurrentSelection
          date={moment(bookingDate).format('dddd MMMM Do')}
          service={selectedService.title}
          startTime={startTime}
          endTime={endTime}
          price={event.total}
          text=""
          onDateEdit={toggleDatePicker}
          onServiceEdit={changeService}
          onTimeEdit={changeTime}
        />
      </Row>
      <Row className="mt-3 d-flex">
        {isEditMode || showDatePicker || bookingDateTime.isAfter(now) ? renderStep() : renderPastDay()}
      </Row>

      {/* Error Modal */}
      <Modal isOpen={errorModalOpen} toggle={toggleErrorModal}>
        <ModalHeader toggle={toggleErrorModal}>Booking Error</ModalHeader>
        <ModalBody>{errorMessage}</ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggleErrorModal}>
            OK
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={scoreModalOpen} toggle={toggleScoreModal}>
        <ModalHeader toggle={toggleScoreModal}>Enter League Score</ModalHeader>
        <ModalBody>
          {scoreLoadError && <div className="text-danger small mb-2">{scoreLoadError}</div>}
          <FormGroup className="mb-0">
            <Label for="leagueScoreInput" className="fw-semibold">Score</Label>
            <Input
              id="leagueScoreInput"
              type="number"
              step="1"
              value={leagueScoreValue}
              onChange={(e) => setLeagueScoreValue(e.target.value)}
              placeholder="Enter score"
              disabled={scoreSaving}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleScoreModal} disabled={scoreSaving}>
            Cancel
          </Button>
          <Button color="primary" onClick={saveLeagueScore} disabled={scoreSaving}>
            {scoreSaving ? 'Saving...' : 'Save Score'}
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default BookingForm;
